import { selectIsCampaignPerformanceAlertsFeatureFlagOn } from 'core/userContext/selectors';
import { useGetAlertMetrics } from 'modules/campaigns/alerts/hooks';
import { useProjectCampaignAlertsQuery } from 'modules/campaigns/alerts/hooks/useProjectCampaignAlertsQuery';
import { useQueryCampaignAlerts } from 'modules/campaigns/editor/hooks';
import { AlertTakeoverCampaign } from 'modules/campaigns/lib/takeovers/AlertTakeover/types';
import { getAlertsToDisplay } from 'modules/campaigns/lib/utils/alertUtils';
import { ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AlertOverridesContext, AlertOverridesContextState } from './AlertOverridesContext';
import { getOverridesFormInitialValues } from './AlertOverridesProvider.utils';

type AlertOverridesProviderProps = {
  campaignData: AlertTakeoverCampaign;
  children: ReactNode;
};

export const AlertOverridesProvider = ({ campaignData, children }: AlertOverridesProviderProps) => {
  const { campaignType, messageMedium, campaignId } = campaignData;

  const isCampaignPerformanceAlertsFeatureEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );

  /** Project campaign alerts */

  const projectAlertsQueryOptions = useMemo(
    () => ({
      skip: !isCampaignPerformanceAlertsFeatureEnabled,
      variables: {
        campaignType,
        messageMedium,
      },
    }),
    [isCampaignPerformanceAlertsFeatureEnabled, campaignType, messageMedium],
  );

  const { isLoading: isLoadingProjectCampaignAlerts, results: projectCampaignAlerts } =
    useProjectCampaignAlertsQuery(projectAlertsQueryOptions);

  /** Campaign alerts */

  const campaignAlertsQueryOptions = useMemo(
    () => ({
      skip: !campaignId || !isCampaignPerformanceAlertsFeatureEnabled,
      variables: { campaignId },
    }),
    [campaignId, isCampaignPerformanceAlertsFeatureEnabled],
  );

  const {
    isLoading: isLoadingCampaignAlerts,
    refetch: refetchCampaignAlerts,
    results: campaignAlerts,
  } = useQueryCampaignAlerts(campaignAlertsQueryOptions);

  /** Alert metrics */
  const metricQueryOptions = { variables: { page: 'CampaignPerformanceAlerts', messageMedium } };
  const { allAlertMetrics, isLoadingMetrics } = useGetAlertMetrics(metricQueryOptions);
  const isLoadingOverrides =
    isLoadingMetrics || isLoadingProjectCampaignAlerts || isLoadingCampaignAlerts;

  /** Form values */

  const projectDefaults = projectCampaignAlerts[messageMedium];
  const alertOverrides = getAlertsToDisplay(campaignAlerts, projectDefaults);

  const defaultValues = useMemo(() => {
    if (!isLoadingOverrides) {
      return getOverridesFormInitialValues(
        allAlertMetrics,
        campaignType,
        alertOverrides,
        messageMedium,
      );
    }
  }, [allAlertMetrics, isLoadingOverrides, alertOverrides, campaignType, messageMedium]);

  const projectDefaultValues = useMemo(() => {
    if (!isLoadingMetrics && !isLoadingProjectCampaignAlerts) {
      return getOverridesFormInitialValues(
        allAlertMetrics,
        campaignType,
        projectDefaults,
        messageMedium,
      );
    }
  }, [
    allAlertMetrics,
    isLoadingMetrics,
    isLoadingProjectCampaignAlerts,
    projectDefaults,
    campaignType,
    messageMedium,
  ]);

  const value: AlertOverridesContextState = {
    defaultValues,
    metrics: allAlertMetrics[messageMedium],
    isLoadingOverrides,
    projectDefaultValues,
    campaignAlerts,
    refetchCampaignAlerts,
  };

  return <AlertOverridesContext.Provider value={value}>{children}</AlertOverridesContext.Provider>;
};
